import type React from "react"
import { useState, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, ChevronDown } from "lucide-react"
// import Mpv2025Prospectus from "/MPV Distributor Prospectus 2025 Interactive.pdf";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const navItems = [
    { name: "Home", href: "/" },
    { name: "Models", href: "/models" },
    { name: "Attachments", href: "/attachments" },
    {
      name: "Applications",
      href: "/applications",
      dropdown: [
        { name: "Overview", href: "/applications" },
        { name: "Ground Care", href: "/applications/ground-care" },
        { name: "Road Maintenance", href: "/applications/road-maintenance" },
        { name: "Winter Operations", href: "/applications/winter" },
        { name: "Municipal Services", href: "/applications/municipal" },
        { name: "Airport Operations", href: "/applications/airport" },
        { name: "Facility Management", href: "/applications/facility-management" },
      ],
    },
    { name: "Partners", href: "/partners" },
    { name: "About", href: "/about" },
    // { name: "Contact", href: "/contact" },
  ]

  const isActive = (href: string) => {
    if (href === "/applications") {
      return location.pathname.startsWith("/applications")
    }
    return location.pathname === href
  }

  // Only used for mobile
  const handleDropdownToggle = (itemName: string) => {
    setActiveDropdown(activeDropdown === itemName ? null : itemName)
  }

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/images/mpv-logo.png"
              alt="MPV Multipurpose Vehicles"
              className="h-16 w-36 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
            {navItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.dropdown ? (
                  <div className="relative">
                    <button
                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors group ${
                        isActive(item.href)
                          ? "text-red-600 bg-red-50"
                          : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                      }`}
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" />
                    </button>

                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-0 mt-1 w-56 rounded-lg shadow-lg border border-gray-200 bg-white py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            location.pathname === dropdownItem.href
                              ? "text-red-600 bg-red-50"
                              : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                          }`}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isActive(item.href)
                        ? "text-red-600 bg-red-50"
                        : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex">
            <Link
              to="/contact"
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm hover:shadow"
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden border-t bg-white">
            <div className="py-4 space-y-1">
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(item.name)}
                        className={`flex items-center justify-between w-full px-4 py-2 font-medium transition-colors ${
                          isActive(item.href)
                            ? "text-red-600 bg-red-50"
                            : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                        }`}
                      >
                        {item.name}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${activeDropdown === item.name ? "rotate-180" : ""}`}
                        />
                      </button>
                      {activeDropdown === item.name && (
                        <div className="pl-4 space-y-1">
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              onClick={() => {
                                setIsOpen(false)
                                setActiveDropdown(null)
                              }}
                              className={`block px-4 py-2 text-sm transition-colors ${
                                location.pathname === dropdownItem.href
                                  ? "text-red-600 bg-red-50"
                                  : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                              }`}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-2 font-medium transition-colors ${
                        isActive(item.href)
                          ? "text-red-600 bg-red-50"
                          : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="px-4 pt-4">
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm hover:shadow"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Navbar
